import { HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpService, UrlService } from '@boels/helpers';
import {
  Cart,
  CartModification,
  CartUrlEnum,
  EntryGroup,
  EntryGroupUpdate,
  GroupOrderModification,
  Order,
  UserEnum,
} from '@boels/models';
import {
  catchError,
  filter,
  NEVER,
  Observable,
  switchMap,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { ApiContextService } from '../../services/api-context/api-context.service';
import { CartManagementService } from '../../services/cart-management/cart-management.service';

@Injectable({
  providedIn: 'any',
})
export class CartGatewayService {
  private readonly httpService = inject(HttpService);
  private urlService = inject(UrlService);
  private apiContextService = inject(ApiContextService);
  private cartManagementService = inject(CartManagementService);

  createCart(): Observable<Cart> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
        ];

        const mergeCart =
          cartUser.type === UserEnum.B2C_AUTHENTICATED && cartId;
        if (mergeCart) params.push(['oldCartId', cartId]);
        else params.push(['fields', 'DEFAULT']);

        const url = this.urlService.combineUri(
          baseSite.uid,
          this.cartManagementService.getCartUrl(
            cartUser.type,
            CartUrlEnum.CREATE,
          ),
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.post<Cart>(url, {}, params, httpHeaders).pipe(
          tap((cart: Cart) => {
            if (mergeCart) this.cartManagementService.removeLocalCart(baseSite);
            else
              this.cartManagementService.storeLocalCart(
                baseSite,
                cartUser.type,
                cart.guid,
              );
          }),
          catchError((response) => {
            if (cartId && baseSite.uid) {
              this.cartManagementService.registerLocalCartAsGuest(
                cartId,
                baseSite.uid,
              );
            }
            return throwError(() => new Error(response.error.message));
          }),
        );
      }),
    );
  }

  getCart(): Observable<Cart> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['fields', 'FULL'],
          ['grouped', 'TRUE'],
          ['lang', language],
          ['curr', currency],
          ['timestamp', new Date().getTime()],
        ];
        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          cartId,
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.get<Cart>(url, params, httpHeaders).pipe(
          tap((cart: Cart) =>
            this.cartManagementService.setCartId(cartUser.type, cart.guid),
          ),
          catchError((response) => {
            this.cartManagementService.removeLocalCart(baseSite);
            return throwError(() => new Error(response.error.message));
          }),
        );
      }),
    );
  }

  getCartReference(referenceType: string): Observable<any> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['referenceType', referenceType],
          ['lang', language],
          ['curr', currency],
        ];

        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          cartId,
          'cartCrossSelling',
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.get(url, params, httpHeaders);
      }),
    );
  }

  addEntry(entry: EntryGroup): Observable<GroupOrderModification> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        if (cartUser.type === UserEnum.ANONYMOUS && !cartId) {
          return this.createCart().pipe(switchMap(() => this.addEntry(entry)));
        }
        const params = [
          ['lang', language],
          ['curr', currency],
        ];
        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          cartId,
          'entries',
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.post<GroupOrderModification>(
          url,
          entry,
          params,
          httpHeaders,
        );
      }),
    );
  }

  removeEntry(entryNumber: number): Observable<any> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
          ['grouped', 'TRUE'],
        ];
        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          cartId,
          'entries',
          entryNumber.toString(),
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.delete(url, params, httpHeaders);
      }),
    );
  }

  updateEntry(
    entryUpdate: EntryGroupUpdate,
  ): Observable<GroupOrderModification> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
        ];
        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          cartId,
          'entries',
          entryUpdate.entryNumber.toString(),
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.put<GroupOrderModification>(
          url,
          entryUpdate,
          params,
          httpHeaders,
        );
      }),
    );
  }

  getReservationCalendar(startDate: string, endDate: string): Observable<any> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['periodStart', startDate],
          ['periodEnd', endDate],
          ['lang', language],
          ['curr', currency],
        ];
        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          cartId,
          'reservationCalendar',
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.get(url, params, httpHeaders);
      }),
    );
  }

  updateCartPointOfService(
    pointOfServiceName: string,
    useRentalWall?: boolean,
  ): Observable<CartModification> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
          ['fields', 'entry(entryNumber,deliveryPointOfService),statusCode'],
        ];

        if (pointOfServiceName) {
          params.push(['depotName', pointOfServiceName]);
        }
        if (useRentalWall) {
          params.push(['useRentalWall', useRentalWall.toString()]);
        }

        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          cartId,
          'depot',
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.put<CartModification>(
          url,
          null,
          params,
          httpHeaders,
        );
      }),
    );
  }

  updateCartDates(startDate: string, endDate: string): Observable<any> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
        ];

        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          cartId,
          'startenddates',
        );

        const object = {
          startDate: startDate,
          endDate: endDate,
        };
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.put(url, object, params, httpHeaders);
      }),
    );
  }

  postRemarks(remarks: string) {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
        ];
        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          cartId,
          'remarks',
        );

        let httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        if (!httpHeaders) httpHeaders = new HttpHeaders();
        httpHeaders = httpHeaders.set('Content-Type', 'application/json');
        return this.httpService.put(url, { remarks }, params, httpHeaders);
      }),
    );
  }

  postNewsletter(newsletter: boolean) {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
        ];
        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          cartId,
          'newsletter',
        );
        let httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        if (!httpHeaders) httpHeaders = new HttpHeaders();
        httpHeaders = httpHeaders.set('Content-Type', 'application/json');
        return this.httpService.put(
          url,
          { newsletterOptIn: newsletter },
          params,
          httpHeaders,
        );
      }),
    );
  }

  placeOrderWithRecaptcha(
    termsChecked: boolean,
    recaptchaToken: string,
  ): Observable<Order> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const returnCartId =
          cartUser.type === UserEnum.B2C_AUTHENTICATED
            ? 'current'
            : (cartId as string);
        const params = [
          ['grouped', 'TRUE'],
          ['fields', 'FULL'],
          ['cartId', returnCartId],
          ['termsChecked', termsChecked.toString()],
          ['recaptchaToken', recaptchaToken],
          ['lang', language],
          ['curr', currency],
        ];
        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.ORDERS,
        );
        const url = this.urlService.combineUri(baseSite.uid, cartSegment);
        let httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        if (!httpHeaders) httpHeaders = new HttpHeaders();
        httpHeaders = httpHeaders.set(
          'Content-Type',
          'application/x-www-form-urlencoded',
        );
        return this.httpService
          .post<Order>(url, {}, params, httpHeaders)
          .pipe(
            tap(() =>
              this.cartManagementService.removeLocalCart(baseSite, true),
            ),
          );
      }),
    );
  }

  assignCartToAnonymous(): Observable<Cart> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(this.cartManagementService.getCartUser()),
      switchMap(([[baseSite, language, currency], cartUser]) => {
        if (cartUser.type !== UserEnum.B2C_AUTHENTICATED) return NEVER;

        const params = [
          ['lang', language],
          ['curr', currency],
        ];
        const cartSegment = this.cartManagementService.getCartUrl(
          cartUser.type,
          CartUrlEnum.CARTS,
        );
        const url = this.urlService.combineUri(
          baseSite.uid,
          cartSegment,
          'anonymous',
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService
          .put<Cart>(url, undefined, params, httpHeaders)
          .pipe(
            tap((cart: Cart) => {
              this.cartManagementService.storeLocalCart(
                baseSite,
                UserEnum.ANONYMOUS,
                cart.guid,
                'registerdToAnonymous',
              );
            }),
          );
      }),
    );
  }
}
